import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_98738aa4-d15a-49a5-91ed-b440272a52f8/artifacts/ycwq5ecm_489609005_1216574520471089_8830070208935895582_n.jpg",
    alt: "BMW M - Detailing Complet",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_98738aa4-d15a-49a5-91ed-b440272a52f8/artifacts/ycwq5ecm_489609005_1216574520471089_8830070208935895582_n.jpg",
    alt: "Lamborghini Huracan - Protecție Ceramică",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_98738aa4-d15a-49a5-91ed-b440272a52f8/artifacts/rodv5nnt_527769456_1313643144097559_2828953638639799113_n.jpg",
    alt: "Porsche Cayman - Polish Profesional",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lsk-auto-styling-1/artifacts/dppje5mc_517438619_1298323068962900_8116375658812081993_n.jpg",
    alt: "Ferrari - Detailing Premium",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lsk-auto-styling-1/artifacts/os5kwx2z_518363580_1304672494994624_5912822775111276324_n.jpg",
    alt: "BMW Serie 1 - Folie PPF",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lsk-auto-styling-1/artifacts/i86lpddf_494752384_1237139491747925_2576095291396262963_n.jpg",
    alt: "BMW Serie 5 - Protecție Ceramică",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lsk-auto-styling-1/artifacts/f2j97p7r_495675201_1246283217500219_8886266255766947568_n.jpg",
    alt: "BMW - Colantare Custom",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_lsk-auto-styling-1/artifacts/ysw4rzqd_495575064_1246474497481091_5684810050069620295_n.jpg",
    alt: "Bentley Flying Spur - Detailing Luxury",
  },
];

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="pt-20 min-h-screen bg-black" data-testid="gallery-page">
      {/* Header */}
      <section className="py-20 page-header-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Galerie <span className="text-red-600">Foto</span>
          </motion.h1>
          <motion.div
            className="red-line mx-auto mb-6"
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          <motion.p
            className="text-base md:text-lg font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            Rezultatele muncii noastre vorbesc de la sine.
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, i) => (
              <motion.div
                key={i}
                className="gallery-item relative aspect-[4/3] overflow-hidden cursor-pointer border border-[#222] hover:border-red-600"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                viewport={{ once: true }}
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${i}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  loading="lazy"
                />
                <div className="gallery-overlay absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3">
                  <ZoomIn className="w-8 h-8 text-red-600" />
                  <span
                    className="text-white text-xs tracking-[0.15em] uppercase font-medium text-center px-4"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    {image.alt}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/95 lightbox-backdrop flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-red-600 transition-colors z-10"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close-btn"
              aria-label="Inchide"
            >
              <X className="w-8 h-8" />
            </button>

            <motion.div
              className="relative max-w-5xl w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full max-h-[85vh] object-contain"
              />
              <p
                className="text-center mt-4 text-white text-sm tracking-[0.1em] font-light"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {selectedImage.alt}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
